import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useFeaturedArches,
  useFeaturedEntities,
  useCategories,
  useEntityCategories,
} from '../hooks';
import { SearchBar, ArcheGrid, EntityGrid, CategoryNav } from '../components';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

type TabValue = 'arches' | 'entities';

export function HomePage() {
  const [activeTab, setActiveTab] = useState<TabValue>('arches');
  const { arches, loading: archesLoading } = useFeaturedArches(12);
  const { entities, loading: entitiesLoading } = useFeaturedEntities(12);
  const { categories: archeCategories, loading: archeCategoriesLoading } = useCategories();
  const { categories: entityCategories, loading: entityCategoriesLoading } = useEntityCategories();

  // Select loading state based on active tab
  const categoriesLoading =
    activeTab === 'arches' ? archeCategoriesLoading : entityCategoriesLoading;

  // Map entity categories to the format expected by CategoryNav
  const mappedCategories =
    activeTab === 'entities'
      ? entityCategories.map((c) => ({
          ...c,
          archeCount: c.entityCount,
        }))
      : archeCategories;

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-linear-to-r from-primary to-primary/80 text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">The Arche™ Store</h1>
            <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
              Discover AI-powered Arche applications and Entities, built by the community. Run
              Arches instantly, no setup required.
            </p>
            <div className="max-w-2xl mx-auto">
              <SearchBar large placeholder="Search the Arche Store..." />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button asChild>
                <Link to="/marketplace/browse">Browse All</Link>
              </Button>
              {!categoriesLoading && mappedCategories.length > 0 && (
                <CategoryNav categories={mappedCategories.slice(0, 5)} />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Section with Tabs */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">Featured</h2>
              <TabsList>
                <TabsTrigger value="arches">
                  Arches ({archesLoading ? '...' : arches.length})
                </TabsTrigger>
                <TabsTrigger value="entities">
                  Entities ({entitiesLoading ? '...' : entities.length})
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="arches" className="mt-0">
              <ArcheGrid arches={arches} loading={archesLoading} />
            </TabsContent>

            <TabsContent value="entities" className="mt-0">
              <EntityGrid entities={entities} loading={entitiesLoading} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Types Overview */}
      {!categoriesLoading && mappedCategories.length > 0 && (
        <section className="py-12 bg-card">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-foreground mb-6">Browse by Type</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {mappedCategories.map((category) => (
                <Link
                  key={category.categoryId}
                  to={`/marketplace/browse?categoryId=${category.categoryId}&tab=${activeTab}`}
                >
                  <Card className="p-6 hover:bg-accent transition-colors">
                    <h3 className="font-semibold text-card-foreground mb-1">{category.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {category.archeCount}{' '}
                      {activeTab === 'arches'
                        ? category.archeCount !== 1
                          ? 'arches'
                          : 'arche'
                        : category.archeCount !== 1
                          ? 'entities'
                          : 'entity'}
                    </p>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer CTA */}
      <section className="py-16 bg-secondary text-secondary-foreground">
        <div className="max-w-7xl mx-auto px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-4">Ready to build your own Arche?</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Join the Fias community and create AI-powered applications that anyone can use.
          </p>
          <Button asChild size="lg">
            <Link to="/contributor/dashboard">Become a Contributor</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
